import React from 'react';

// THE shared university-branding kit (founder, 2026-08-13: "whenever we show
// something about a university in any page, the logo and the color themes
// have to flow"). One component + helpers so every surface renders a school
// the same way — extracted from CampusEventsPage, which pioneered the form.
//
// The rules (skill `university-branding`):
//  - Colour is DECORATION and never meaning. Status (fit band, seat state,
//    deadline urgency) keeps its own palette everywhere — nobody should have
//    to know a college's colours to read whether they can get in.
//  - No verified colours → the app's own neutral. Never invent a colour.
//  - Fetch branding per UNIVERSITY, not per row (`branding_for` server-side:
//    a {university_id: {primary, secondary, logo_url}} map).

const BRAND_FALLBACK = { primary: '#1A4D2E', secondary: '#E0DED8' };

export const brandOf = (b) => ({ ...BRAND_FALLBACK, ...(b || {}) });

// Initials for the crest when a college has no hosted logo. Drops the filler
// words so "University of California, San Diego" reads UCSD, not UOCSD.
export function crestInitials(name = '') {
    const words = String(name).replace(/[,.]/g, ' ').split(/\s+/)
        .filter((w) => w && !/^(of|the|at|and|in|for|de|du)$/i.test(w));
    return words.map((w) => w[0]).join('').toUpperCase().slice(0, 4) || '?';
}

// The two-colour band treatment shared with the Launchpad card.
export const brandBand = (branding) => {
    const b = brandOf(branding);
    return `linear-gradient(90deg, ${b.primary} 0 66%, ${b.secondary} 66% 100%)`;
};

export function Crest({ name, branding, size = 44 }) {
    const b = brandOf(branding);
    const px = `${size}px`;
    if (b.logo_url) {
        return (
            <img
                src={b.logo_url}
                alt=""                          // decorative: the name is right beside it
                // lazy: React's server render otherwise emits a preload for
                // every logo (another origin), and on a college page that
                // preload competed with the hero photo for a phone's
                // bandwidth (#2605); the /colleges index has one per card.
                loading="lazy"
                className="rounded-full object-contain bg-white border border-[#E0DED8] shrink-0"
                style={{ width: px, height: px }}
            />
        );
    }
    return (
        <div
            className="rounded-full grid place-items-center font-bold text-white shrink-0"
            style={{ width: px, height: px, background: b.primary,
                     fontSize: `${Math.max(10, size / 3.4)}px` }}
            aria-hidden="true"
        >
            {crestInitials(name)}
        </div>
    );
}
